"use client";

import React, { useState, useEffect } from "react";
import { ResponsivePopover } from "@/components/ui/responsive-popover";
import { getQuickFilterFields } from "../utils/filter-rendering";
import { QuickFiltersPanel, type QuickFiltersAdvancedProps } from "./QuickFiltersPanel";
import type { SimpleTableConfig, BaseEntity } from "../types";

interface QuickFiltersPopoverProps<TData extends BaseEntity> {
  config: SimpleTableConfig<TData>;
  simpleFilters: Record<string, unknown>;
  onFiltersChange: (newFilters: Record<string, unknown>) => void;
  onCustomRangeOpen: (fieldId: string, fieldLabel: string) => void;
  activeFilterCount: number;
  advancedFilterCount: number;
  trigger: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When true, open directly in Advanced Filters mode (e.g. from More menu) */
  openInAdvancedMode?: boolean;
  onConsumedOpenInAdvancedMode?: () => void;
  /** Advanced filter props - required when showing advanced filters in popover */
  advancedFilterProps?: QuickFiltersAdvancedProps;
}

/**
 * Quick Filters Popover - unified desktop filter entry point.
 * The panel body itself is `QuickFiltersPanel`, shared with the mobile filter
 * sheet so both surfaces expose the same filter set.
 */
export function QuickFiltersPopover<TData extends BaseEntity>({
  config,
  simpleFilters,
  onFiltersChange,
  onCustomRangeOpen,
  activeFilterCount,
  advancedFilterCount,
  trigger,
  open,
  onOpenChange,
  openInAdvancedMode,
  onConsumedOpenInAdvancedMode,
  advancedFilterProps,
}: QuickFiltersPopoverProps<TData>) {
  const [mode, setMode] = useState<"quick" | "advanced">("quick");

  useEffect(() => {
    if (open && openInAdvancedMode) {
      setMode("advanced");
      onConsumedOpenInAdvancedMode?.();
    }
  }, [open, openInAdvancedMode, onConsumedOpenInAdvancedMode]);

  useEffect(() => {
    if (!open) {
      setMode("quick");
    }
  }, [open]);

  const hasQuickFilters = getQuickFilterFields<TData>(config).length > 0;
  const hasAdvancedFilterProps = !!advancedFilterProps;

  if (!hasQuickFilters && !hasAdvancedFilterProps) {
    return null;
  }

  const isAdvancedMode = mode === "advanced";

  const panel = (
    <div className={isAdvancedMode ? undefined : "min-w-[280px] max-w-[90vw]"}>
      <QuickFiltersPanel<TData>
        config={config}
        simpleFilters={simpleFilters}
        onFiltersChange={onFiltersChange}
        onCustomRangeOpen={onCustomRangeOpen}
        activeFilterCount={activeFilterCount}
        advancedFilterCount={advancedFilterCount}
        mode={mode}
        onModeChange={setMode}
        advancedFilterProps={advancedFilterProps}
      />
    </div>
  );

  return (
    <ResponsivePopover forcePopover
      open={open}
      onOpenChange={onOpenChange}
      content={panel}
      title={isAdvancedMode ? "Advanced Filters" : "Quick Filters"}
      align="start"
      popoverSide="bottom"
      className={
        isAdvancedMode
          ? "w-[min(640px,90vw)] max-h-[85vh] overflow-y-auto p-4"
          : "w-[min(600px,95vw)] max-h-[80vh] overflow-y-auto p-4"
      }
      showCloseButton={false}
      asChild
    >
      {/* Controlled ResponsivePopover: the trigger owns opening (the desktop
          Mantine target injects no toggle when `open` is supplied). The caller
          passes a plain node, so the click is handled on this wrapper. */}
      <span className="inline-flex" onClick={() => onOpenChange?.(!open)}>
        {trigger}
      </span>
    </ResponsivePopover>
  );
}
